import { generatePath } from 'react-router';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import {
  ADD_PROJECT,
  ADD_PROJECT_FAILURE,
  ADD_PROJECT_SUCCESS,
  CLEAR_EDIT_PROJECT,
  DELETE_PROJECT,
  DELETE_PROJECT_FAILURE,
  DELETE_PROJECT_SUCCESS,
  EDIT_PROJECT,
  FETCH_PROJECTS,
  FETCH_PROJECTS_FAILURE,
  FETCH_PROJECTS_SUCCESS,
  PROJECTS_CLEAR_STATE,
  PROJECTS_STATUSES_UPDATED,
  RENAME_PROJECT,
  RENAME_PROJECT_FAILURE,
  RENAME_PROJECT_SUCCESS,
  CLEAR_CREATED_PROJECT,
  RETRY_PROJECT,
  RETRY_PROJECT_SUCCESS,
  RETRY_PROJECT_FAILURE,
  IMPORT_PROJECTS_SUCCESS,
  CLEAR_IMPORTED_PROJECTS,
  IMPORT_PROJECTS,
  PROJECTS_LIST_DEFAULT_SIZE,
  PROJECTS_LIST_PAGE_SIZE_CACHE_KEY,
  SET_VIEW_PROJECTS_POSITION,
} from '@Pages/RetroSynthesis/RetroSynthesisProjects/constants';
import { noCache } from '@Utils/Network';
import store from '@Store';
import { getCombinedRouteParams, getTrackingPathName } from '@AppRoutes/AppRoutesSelectors';
import SocketBroker from '@Services/SocketBroker';
import { RxSocketUrls } from '@Constants/CommonConstants';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import capitalize from 'lodash/capitalize';
import {
  onProjectsListLoaded,
  onProjectStatusChanged,
  getOriginPageTypeFromNgRoute,
  getSourceKeyHeaderForStatistica,
  onProjectExtensionStarted,
} from '@Pages/RetroSynthesis/RetroSynthesisProjects/analytics';
import { getProjects, getProjectsListResponse } from '@Pages/RetroSynthesis/RetroSynthesisProjects/selectors';
import first from 'lodash/first';
import { RouteType, ProjectSynthesisJobType, ProjectStatus } from '@Pages/RetroSynthesis/constants';
import { mapProjectIdsForLiveUpdates, populateChildProjects, collectChildIds } from '@Pages/RetroSynthesis/RetroSynthesisProjects/utils';
import { projectRoutesCacheKey } from '@Pages/RetroSynthesis/ProjectRoutes/constants';
import namespacedCache from '@Services/NamespacedCache';
import AppStorage from '@Services/AppStorage';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { CLEAR_PROJECTS_COUNT } from '@Pages/RetroSynthesis/RsBreadcrumbs/constants';
import identity from 'lodash/identity';
import isNil from 'lodash/isNil';

const fetchProjectsFailureAction = (payload) => ({
  type: FETCH_PROJECTS_FAILURE,
  payload,
});

const socketUnsubscribeCallbacks = {
  [RouteType.PUBLISHED]: null,
  [RouteType.PREDICTED]: null,

  callAndClearAll() {
    if (this[RouteType.PUBLISHED]) {
      this[RouteType.PUBLISHED]();
      this[RouteType.PUBLISHED] = null;
    }

    if (this[RouteType.PREDICTED]) {
      this[RouteType.PREDICTED]();
      this[RouteType.PREDICTED] = null;
    }
  },
};

// Store pending setTimeout IDs for cleanup
const pendingTimeouts = [];

const clearPendingTimeouts = () => {
  while (pendingTimeouts.length > 0) {
    const timeoutId = pendingTimeouts.pop();
    clearTimeout(timeoutId);
  }
};

// Don't clear on projects unmount, it will break breadcrumbs and edit project functionality
// Is used in LoginService.afterAuthRedirect()
export const projectsClearStateAction = () => (dispatch) => {
  try {
    clearPendingTimeouts();
    socketUnsubscribeCallbacks.callAndClearAll();
  } catch (error) {
    Logger.error('Unsubscribe from live updates error:', error);
  } finally {
    dispatch({ type: PROJECTS_CLEAR_STATE });
  }
  // reset RS projects count for breadcrumbs
  dispatch({ type: CLEAR_PROJECTS_COUNT });
};

export const publishedProjectUpdatesCallback = (dispatch, getState) => (data) => {
  const { payload } = data || {};

  const { projectId, publishedStatus } = payload || {};

  if (!projectId) {
    return;
  }

  const { content = [] } = getProjectsListResponse(getState()) || {};
  const { publishedStatus: prevPublishedStatus } = first(content.filter((project) => project.id === projectId)) || {};

  if (publishedStatus !== prevPublishedStatus) {
    dispatch({
      type: PROJECTS_STATUSES_UPDATED,
      payload,
    });

    // Clear cache
    namespacedCache.removeByKey(projectRoutesCacheKey(projectId));

    // AA
    onProjectStatusChanged(payload);
  }
};

export const initRoutesProcessing = async (projectId) => {
  try {
    // Call BE to initiate routes processing
    await MessageBroker.send(
      RequestUrls.rsProjectRoutesProcess,
      {
        id: projectId,
      },
      noCache()
    );
  } catch (error) {
    Logger.error('rsProjectRoutesProcess - call error: ', error);
  }
};

const getProjectFromContent = (projectId, getState) => {
  const { content = [] } = getProjectsListResponse(getState()) || {};
  // get project using projectId or childId
  let project = first(content.filter((p) => p.id === projectId || (p.childIds && p.childIds.includes(projectId)))) || {};
  if (project.id !== projectId && project.childIds && project.childProjects) {
    project = project.childProjects.find((childProject) => childProject.id === projectId);
  }
  return project;
};

export const predictedProjectUpdatesCallback = (dispatch, getState) => async (data) => {
  const { payload } = data || {};

  const { projectId, rtStatus, position, childId, parentId } = payload || {};

  if (!projectId) {
    return;
  }

  const { rtStatus: prevRtStatus, queuePosition: prevPosition } = getProjectFromContent(projectId, getState);

  if (rtStatus !== prevRtStatus || (rtStatus === ProjectStatus.IN_QUEUE && !isNil(position) && prevPosition !== position.toString())) {
    dispatch({
      type: PROJECTS_STATUSES_UPDATED,
      payload,
    });

    // Clear cache
    namespacedCache.removeByKey(projectRoutesCacheKey(projectId));

    // AA
    onProjectStatusChanged(payload);

    if (childId && parentId) {
      // fetch child project from live update
      // eslint-disable-next-line no-use-before-define
      dispatch(fetchChildProjectFromLiveUpdate(parentId, childId));
    }

    if (rtStatus !== prevRtStatus && rtStatus === ProjectStatus.NEEDS_PROCESSING) {
      // if status is NEED_PROCESSING need to call BE http API for processing routes start
      const timeoutId = setTimeout(() => {
        initRoutesProcessing(projectId);
      });
      pendingTimeouts.push(timeoutId);
    }
  }
};

export const projectsLiveUpdates = (projects) => (dispatch, getState) => {
  const mappedIds = mapProjectIdsForLiveUpdates(projects);

  // Published
  if (!isEmpty(mappedIds[RouteType.PUBLISHED])) {
    if (!socketUnsubscribeCallbacks[RouteType.PUBLISHED]) {
      socketUnsubscribeCallbacks[RouteType.PUBLISHED] = SocketBroker.observe(
        RxSocketUrls.RetroSynthesis.PublishedProjectsStatusesSubscribe,
        publishedProjectUpdatesCallback(dispatch, getState)
      );
    }
    SocketBroker.send(
      { send: RxSocketUrls.RetroSynthesis.PublishedProjectsStatusesSend },
      {
        params: {
          rtProjectIds: mappedIds[RouteType.PUBLISHED],
        },
      }
    );
  }

  if (!isEmpty(mappedIds[RouteType.PREDICTED])) {
    if (!socketUnsubscribeCallbacks[RouteType.PREDICTED]) {
      socketUnsubscribeCallbacks[RouteType.PREDICTED] = SocketBroker.observe(
        RxSocketUrls.RetroSynthesis.PredictedProjectsStatusesSubscribe,
        predictedProjectUpdatesCallback(dispatch, getState)
      );
    }

    SocketBroker.send(
      { send: RxSocketUrls.RetroSynthesis.PredictedProjectsStatusesSend },
      {
        params: {
          rtProjectIds: mappedIds[RouteType.PREDICTED],
        },
      }
    );
  }
};

export const fetchChildProjectFromLiveUpdate = (parentId, childId) => async (dispatch, getState) => {
  const childProject = await MessageBroker.send(RequestUrls.rsProjectsChildList, { childIds: [childId], parentIds: [parentId] }, noCache());
  const response = getProjectsListResponse(getState()) || {};
  const content = response.content || [];
  const project = first(content.filter((p) => p.id === parentId)) || {};
  project.childIds = project.childIds || [];
  project.childIds.push(childId);
  populateChildProjects(content, childProject);
  // Update response with child projects
  dispatch({
    type: FETCH_PROJECTS_SUCCESS,
    payload: { ...response, content: [...content] },
  });
  // AA tracking
  onProjectExtensionStarted(childProject[0]);
  // Listen for child projects live updates
  dispatch(projectsLiveUpdates(content));
};

export const fetchProjectsAction =
  (params, liveUpdates = false) =>
  async (dispatch) => {
    dispatch({ type: FETCH_PROJECTS });
    try {
      // Check session storage for projects list size and set default size if not present
      const listSize = params.size ? params.size : AppStorage.instance.get(PROJECTS_LIST_PAGE_SIZE_CACHE_KEY) || PROJECTS_LIST_DEFAULT_SIZE;
      // Workaround to fix issue with cached by browser or MessageBroker request
      const payload = await MessageBroker.send(RequestUrls.rsProjectsList, { ...params, size: listSize }, noCache());
      // Update session storage page size
      AppStorage.instance.set(PROJECTS_LIST_PAGE_SIZE_CACHE_KEY, listSize);

      const ids = collectChildIds(payload.content);
      if (ids.allChildIds && ids.allChildIds.length) {
        const childProjects = await MessageBroker.send(
          RequestUrls.rsProjectsChildList,
          { childIds: ids.allChildIds, parentIds: ids.allParentIds },
          noCache()
        );
        populateChildProjects(payload.content, childProjects);
      }

      dispatch({
        type: FETCH_PROJECTS_SUCCESS,
        payload,
      });

      const { content = [] } = payload || {};

      // Only when fetched from projects page
      // eslint-disable-next-line no-unused-expressions
      liveUpdates && onProjectsListLoaded(content);

      if (liveUpdates) {
        dispatch(projectsLiveUpdates(content));
      }

      // Send processing request for projects with NEEDS_PROCESSING status
      // Should be after subscribing to live updates, so it can pick up status updates
      content.forEach(({ rtStatus, id: projectId }) => {
        if (rtStatus === ProjectStatus.NEEDS_PROCESSING) {
          const timeoutId = setTimeout(() => {
            initRoutesProcessing(projectId);
          });
          pendingTimeouts.push(timeoutId);
        }
      });
    } catch (e) {
      dispatch(fetchProjectsFailureAction(e));
    }
  };

export const addProjectSuccessAction = (response, editorType, isEditProjectMode, trackingContext) => ({
  type: ADD_PROJECT_SUCCESS,
  payload: {
    ...response,
    editorType,
    isEditProjectMode,
    trackingContext,
  },
});

const addProjectFailureAction = (payload) => ({
  type: ADD_PROJECT_FAILURE,
  payload,
});

export const addProjectAction = (params, { editorType, isEditProjectMode, location } = {}) => {
  const combinedParams = getCombinedRouteParams(store.getState());
  const stateCurrentName = getTrackingPathName(store.getState());
  return async (dispatch) => {
    dispatch({ type: ADD_PROJECT });
    try {
      const trackingContext = {
        type: getOriginPageTypeFromNgRoute({
          ngRouteName: stateCurrentName,
          ngRouteParams: combinedParams,
          location,
        }),
      };
      const result = await MessageBroker.send(RequestUrls.rsAddProject, params, {
        ...noCache(),
        headers: {
          sourceKey: getSourceKeyHeaderForStatistica({
            ngRouteName: stateCurrentName,
            ngRouteParams: combinedParams,
            location,
          }),
        },
      });
      if (result.level && result.message) {
        NotifyDialogsService.openModalWindow({
          text: result.message,
          title: capitalize(result.level.toLowerCase()),
          confirmText: 'Ok',
          type: 'warning',
          backdrop: 'static',
          size: 'md',
        });
      } else {
        dispatch(addProjectSuccessAction(result, editorType, isEditProjectMode, trackingContext));
        dispatch({ type: CLEAR_PROJECTS_COUNT });
      }
    } catch (e) {
      dispatch(addProjectFailureAction(e));
    }
  };
};

export const renameProjectAction = (projectId, projectName) => async (dispatch) => {
  dispatch({ type: RENAME_PROJECT });
  try {
    await MessageBroker.send(RequestUrls.rsEditProject, { projectId, projectName } || {});
    const payload = {
      projectName,
    };
    dispatch({ type: RENAME_PROJECT_SUCCESS, payload, projectId });
  } catch (error) {
    dispatch({ type: RENAME_PROJECT_FAILURE, payload: error });
  }
};

export const deleteProjectsAction =
  (projectIdsToDelete = []) =>
  async (dispatch) => {
    dispatch({ type: DELETE_PROJECT });
    try {
      const deleteProjectPromises = projectIdsToDelete.map((id) => MessageBroker.send(RequestUrls.rsDeleteProject, { id }));
      const [deleteProjectsResults] = await Promise.all(deleteProjectPromises);
      dispatch({ type: DELETE_PROJECT_SUCCESS, deleteProjectsResults, projectIds: projectIdsToDelete });
      dispatch({ type: CLEAR_PROJECTS_COUNT });
    } catch (error) {
      dispatch({ type: DELETE_PROJECT_FAILURE, payload: error });
    }
  };

export const bulkDeleteProjectsAction =
  (projectIds = []) =>
  async (dispatch) => {
    dispatch({ type: DELETE_PROJECT });
    try {
      const response = await MessageBroker.send(RequestUrls.rsProjectsBulkDelete, null, noCache(), projectIds);
      dispatch({ type: DELETE_PROJECT_SUCCESS, payload: { ids: projectIds, response }, projectIds });
      dispatch({ type: CLEAR_PROJECTS_COUNT });
    } catch (error) {
      dispatch({ type: DELETE_PROJECT_FAILURE, payload: error });
      NotifyDialogsService.openModalWindow({
        title: 'Deletion Error',
        text: 'Failed to delete Project. Please contact support.',
        type: 'error',
        confirmText: 'Close',
        trackingOptions: {
          message: 'Failed to delete Project. Please contact support.',
          component: 'Retrosynthesis',
        },
      });
    }
  };

export const editProjectAction = (projectToEdit) => ({
  type: EDIT_PROJECT,
  payload: { projectToEdit },
});

export const clearProjectToEditState = () => ({
  type: CLEAR_EDIT_PROJECT,
});

export const goToLoginAction = () => () => {
  AppStorage.instance.set('revisitLastState', true);
  history.push({
    pathname: generatePath(paths.LOGIN),
  });
};

export const clearCreatedProjectState = () => ({
  type: CLEAR_CREATED_PROJECT,
});

export const retrySynthesisJob = (projectId, type) => async (dispatch, getState) => {
  dispatch({ type: RETRY_PROJECT });
  try {
    switch (type) {
      case ProjectSynthesisJobType.PREDICTED: {
        const payload = await MessageBroker.send(RequestUrls.rsRetryProjectPredictedJob, { id: projectId });
        dispatch(projectsLiveUpdates(getProjects(getState())));
        dispatch({ type: RETRY_PROJECT_SUCCESS, payload });
        break;
      }
      case ProjectSynthesisJobType.PUBLISHED: {
        const payload = await MessageBroker.send(RequestUrls.rsRetryProjectPublishedJob, { id: projectId });
        dispatch(projectsLiveUpdates(getProjects(getState())));
        dispatch({ type: RETRY_PROJECT_SUCCESS, payload });
        break;
      }
      default: {
        Logger.error('retrySynthesisJob - invalid synthesis job type: ', type);
        dispatch({ type: RETRY_PROJECT_FAILURE });
      }
    }
  } catch (error) {
    dispatch({ type: RETRY_PROJECT_FAILURE, payload: error });
  }
};

export const retryMultipleSynthesisJob = (projectId, types) => async (dispatch, getState) => {
  dispatch({ type: RETRY_PROJECT });
  if (isEmpty(types)) {
    Logger.error('retryMultipleSynthesisJob - empty synthesis job types');
    dispatch({ type: RETRY_PROJECT_FAILURE });
  } else {
    try {
      if (includes(types, ProjectSynthesisJobType.PUBLISHED)) {
        await MessageBroker.send(RequestUrls.rsRetryProjectPublishedJob, { id: projectId });
      }
      if (includes(types, ProjectSynthesisJobType.PREDICTED)) {
        await MessageBroker.send(RequestUrls.rsRetryProjectPredictedJob, { id: projectId });
      }
      dispatch(projectsLiveUpdates(getProjects(getState())));
      dispatch({ type: RETRY_PROJECT_SUCCESS });
    } catch (error) {
      dispatch({ type: RETRY_PROJECT_FAILURE, payload: error });
    }
  }
};

export const addImportProjectsAction = (params, file) => async (dispatch) => {
  dispatch({ type: IMPORT_PROJECTS });
  try {
    const requestData = new FormData();
    requestData.append(
      'params',
      new Blob([JSON.stringify(params)], {
        type: 'application/json',
      })
    );
    requestData.append('file', file);

    const result = await MessageBroker.send(RequestUrls.rsProjectsImport, requestData, {
      ...noCache(),
      transformRequest: identity,
      headers: { 'Content-Type': undefined },
    });

    dispatch({ type: IMPORT_PROJECTS_SUCCESS, payload: result });
    dispatch({ type: CLEAR_PROJECTS_COUNT });
  } catch (e) {
    dispatch(addProjectFailureAction(e));
  }
};

export const clearImportedProjectsState = () => ({
  type: CLEAR_IMPORTED_PROJECTS,
});

export const setViewProjectsPosition = (position) => ({
  type: SET_VIEW_PROJECTS_POSITION,
  payload: position,
});
